import { useCallback, useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import {
  ChevronRight as CollapseIcon,
  ChevronLeft as ExpandIcon,
  Refresh as RefreshIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import type { GridSortModel } from '@mui/x-data-grid';
import { AggregationPanel } from './AggregationPanel';
import { StudyDataGrid } from './StudyDataGrid';
import { useStudies, useRefreshStudies } from './useStudies';
import { ActionToolbar } from './ActionToolbar';
import { StudyContextMenu } from './StudyContextMenu';
import { ViewerPickerMenu } from './ViewerPickerMenu';
import { useUIStore } from '../../stores/uiStore';
import { useResizableSplitter } from '../../hooks/useResizableSplitter';
import { getAllViewers, getDefaultViewer } from '../viewer/viewerRegistry';
import { gwtStyles } from '../../config/theme';
import type { Study } from '../../api/types';

const PAGE_SIZE = 100;

/**
 * Maps DataGrid column field names to backend search attribute names
 * for ORDER WhereBean sorting.  Values from StudyAttribute.getSearchNames().
 * null = not sortable server-side.
 */
const FIELD_TO_BACKEND_ATTR: Record<string, string | null> = {
  name:            'name',
  id:              'id',
  sex:             'sex',
  dob:             'dob',
  status:          'status',
  acc:             'acc',
  mod:             'mod',
  bod:             'bod',
  date:            'date',
  physician:       'phy',
  ae:              'ae',
  descr:           'descr',
  radiologist:     'radiologist',
  comment:         'comment',
  dicomCount:      'dicomCount',
  siteName:        'sitename',
  history:         'history',
  reasonForExam:   'reason',
  reason:          'reason',
  faxCount:        null,
  finalRead:       null,
};

/** Tab labels for title bar */
const TAB_TITLES: Record<string, string> = {
  studies: 'All Studies',
  newstudy: 'New Studies',
  assigned: 'Assigned Studies',
  dictated: 'Dictated Studies',
  read: 'Read Studies',
};

/* ── Context menu state ── */
interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  studies: Study[];
}

/**
 * GWT-matching worklist panel — mirrors StudyListPanel.java.
 * Wired to real data via useStudies hook + getStudiesPaged RPC.
 */
export const WorklistPanel = () => {
  const activeTab = useUIStore((s) => s.activeTab);
  const page = useUIStore((s) => s.page);
  const setPage = useUIStore((s) => s.setPage);
  const activeFilters = useUIStore((s) => s.activeFilters);
  const quickSearch = useUIStore((s) => s.quickSearch);
  const quickSearchField = useUIStore((s) => s.quickSearchField);
  const aggregationPanelOpen = useUIStore((s) => s.aggregationPanelOpen);
  const toggleAggregationPanel = useUIStore((s) => s.toggleAggregationPanel);
  const setSelectedStudyUids = useUIStore((s) => s.setSelectedStudyUids);
  const setSelectedStudies = useUIStore((s) => s.setSelectedStudies);
  const selectedStudyUids = useUIStore((s) => s.selectedStudyUids);
  const selectedStudies = useUIStore((s) => s.selectedStudies);
  const resultLimit = useUIStore((s) => s.resultLimit);

  const refreshStudies = useRefreshStudies();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  // Viewer picker state (for double-click when multiple viewers are available)
  const [viewerPicker, setViewerPicker] = useState<{
    anchorPosition: { top: number; left: number };
    study: Study;
  } | null>(null);

  // Sort state — persisted in uiStore across sessions
  const sortModel = useUIStore((s) => s.sortModel) as GridSortModel;
  const setSortModelStore = useUIStore((s) => s.setSortModel);
  const setSortModel = useCallback(
    (model: GridSortModel) => {
      // Convert to plain objects for zustand persistence
      setSortModelStore(model.map((m) => ({ field: m.field, sort: m.sort })));
    },
    [setSortModelStore],
  );
  const rawField = sortModel[0]?.field;
  const sortField = rawField ? (FIELD_TO_BACKEND_ATTR[rawField] ?? undefined) : undefined;
  const sortDirection = sortModel[0]?.sort === 'asc' ? 'ASC' as const : sortModel[0]?.sort === 'desc' ? 'DESC' as const : undefined;
  const pageSize = resultLimit ? Math.min(PAGE_SIZE, resultLimit) : PAGE_SIZE;

  // Fetch studies
  const { data, isLoading, isFetching } = useStudies({
    tab: activeTab,
    page,
    pageSize,
    sortField,
    sortDirection,
    filters: activeFilters,
    quickSearch,
    quickSearchField,
  });

  const studies = useMemo(() => data?.records ?? [], [data]);
  const totalCount = resultLimit != null
    ? Math.min(data?.total ?? 0, resultLimit)
    : data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showStart = totalCount > 0 ? page * pageSize + 1 : 0;
  const showEnd = Math.min((page + 1) * pageSize, totalCount);

  // Aggregation panel splitter
  const {
    size: aggWidth,
    splitterProps,
  } = useResizableSplitter({
    direction: 'horizontal',
    initialSize: 220,
    minSize: 150,
    maxSize: 320,
  });

  // Selection — updates both UIDs and full study objects
  const handleSelectionChange = useCallback(
    (uids: string[]) => {
      setSelectedStudyUids(uids);
      const selected = studies.filter((s) => uids.includes(s.uid));
      setSelectedStudies(selected);
    },
    [studies, setSelectedStudyUids, setSelectedStudies],
  );

  const openDynamicTab = useUIStore((s) => s.openDynamicTab);

  /** Open study in a specific viewer */
  const openStudyInViewer = useCallback(
    (study: Study, viewerId?: string) => {
      const vid = viewerId ?? getDefaultViewer().id;
      openDynamicTab({
        id: `viewer-${vid}-${study.uid}`,
        label: `${study.name || 'Study'} - ${study.acc || study.uid}`.slice(0, 40),
        type: 'viewer',
        study,
        viewerId: vid,
        closeable: true,
      });
    },
    [openDynamicTab],
  );

  const handleDoubleClick = useCallback(
    (study: Study, event?: React.MouseEvent) => {
      const viewers = getAllViewers();
      if (viewers.length > 1 && event) {
        // Multiple viewers — show picker at click position
        setViewerPicker({
          anchorPosition: { top: event.clientY, left: event.clientX },
          study,
        });
      } else {
        // Single viewer — open directly
        openStudyInViewer(study);
      }
    },
    [openStudyInViewer],
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, study: Study) => {
      event.preventDefault();
      // If the right-clicked study is already selected, action all selected studies;
      // otherwise, select only the clicked study.
      const isInSelection = selectedStudyUids.includes(study.uid);
      const contextStudies = isInSelection && selectedStudies.length > 1
        ? selectedStudies
        : [study];
      if (!isInSelection) {
        setSelectedStudyUids([study.uid]);
        setSelectedStudies([study]);
      }
      setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, studies: contextStudies });
    },
    [selectedStudyUids, selectedStudies, setSelectedStudyUids, setSelectedStudies],
  );

  const tabTitle = TAB_TITLES[activeTab] || 'Studies';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: gwtStyles.tabContent,
      }}
    >
      {/* Title bar — 32px */}
      <Box
        sx={{
          height: 32,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          backgroundColor: gwtStyles.tabContent,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle }}
        >
          {tabTitle}
        </Typography>
        {isFetching && (
          <Typography
            variant="caption"
            sx={{ position: 'absolute', left: 8, fontSize: 10, color: 'text.secondary' }}
          >
            Loading...
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={toggleAggregationPanel}
          sx={{ position: 'absolute', right: 4, top: 4, width: 24, height: 24 }}
          title={aggregationPanelOpen ? 'Hide filters' : 'Show filters'}
        >
          {aggregationPanelOpen ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>

      {/* Grid + Aggregation split */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', mx: '5px' }}>
        {/* Grid area */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <StudyDataGrid
            studies={studies}
            loading={isLoading}
            onSelectionChange={handleSelectionChange}
            onDoubleClick={handleDoubleClick}
            onContextMenu={handleContextMenu}
            selectedUids={selectedStudyUids}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </Box>

        {/* Splitter + aggregation */}
        {aggregationPanelOpen && (
          <>
            <Box
              {...splitterProps}
              sx={{
                width: 5,
                flexShrink: 0,
                cursor: 'col-resize',
                backgroundColor: 'divider',
                '&:hover': { backgroundColor: 'primary.light' },
              }}
            />
            <Box sx={{ width: aggWidth, flexShrink: 0, overflow: 'auto' }}>
              <AggregationPanel aggregations={data?.aggregations} />
            </Box>
          </>
        )}
      </Box>

      {/* Bottom bar — 32px */}
      <Box
        sx={{
          height: 32,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: gwtStyles.tabContent,
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 0.5,
        }}
      >
        {/* Pager: 0-210px */}
        <Box sx={{ width: 210, display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            size="small"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            sx={{ width: 24, height: 24 }}
          >
            <PrevIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {totalCount > 0
              ? `${showStart}-${showEnd} of ${totalCount.toLocaleString()}`
              : '0 studies'}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            sx={{ width: 24, height: 24 }}
          >
            <NextIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Toolbar: 210px+ */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, overflow: 'hidden' }}>
          <ActionToolbar selectedStudies={selectedStudies} />
          <IconButton
            size="small"
            title="Refresh"
            onClick={refreshStudies}
            sx={{ width: 28, height: 28 }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Context menu */}
      {contextMenu && (
        <StudyContextMenu
          mouseX={contextMenu.mouseX}
          mouseY={contextMenu.mouseY}
          studies={contextMenu.studies}
          onClose={() => setContextMenu(null)}
          onOpenInViewer={(viewerId) => openStudyInViewer(contextMenu.studies[0], viewerId)}
        />
      )}

      {/* Viewer picker (double-click with multiple viewers) */}
      {viewerPicker && (
        <ViewerPickerMenu
          anchorPosition={viewerPicker.anchorPosition}
          study={viewerPicker.study}
          onSelect={(viewerId) => openStudyInViewer(viewerPicker.study, viewerId)}
          onClose={() => setViewerPicker(null)}
        />
      )}
    </Box>
  );
};
